import axios from 'axios'
import Vue from 'vue'
import { getTrackingHeaders } from '@/plugins/tracker'

export const userModel = {
  namespaced: true,
  state () {
    return {
      userObject: null,
      featureFlagsData: [],
      featureFlagsCompanyId: null,
      previewRoleData: null,
    }
  },
  getters: {
    loggedInUser (state) {
      return state.userObject
    },
    defaultCompanyId (state, getters) {
      return getters.loggedInUser.company_users.slice(0, 1).shift().company.id
    },
    companyId (state, getters, rootState, rootGetters) {
      return rootGetters.companyId || getters.defaultCompanyId
    },
    companyUsers (state, getters) {
      let user = getters.loggedInUser
      if (user == null) {
        return null
      }
      return user.company_users
    },
    companyUser (state, getters) {
      return (
        (getters.companyUsers || [])
          .filter((user) => `${user.company.id}` === `${getters.companyId}`)
          .slice(0, 1)
          .shift() || null
      )
    },
    reports (state, getters) {
      return getters.companyUser.role.reports
    },
    permissions (state, getters) {
      if (state.previewRoleData) {
        return state.previewRoleData.permissions || []
      }
      let companyUser = getters.companyUser
      if (companyUser === null) {
        return []
      }
      return companyUser.role.permissions
    },
    isPreviewingRole (state) {
      return state.previewRoleData !== null
    },
    previewRole (state) {
      return state.previewRoleData
    },
    companyName (state, getters) {
      let companyUser = getters.companyUser
      if (companyUser === null || !companyUser.company.display_name) {
        return 'Ingest AI'
      }
      return companyUser.company.display_name
    },
    canViewChecks (state, getters) {
      return getters['permissions'].includes('VIEW_CHECKS')
    },
    canManageAttributes (state, getters) {
      return getters['permissions'].includes('MANAGE_ATTRIBUTES')
    },
    canManageExpressions (state, getters) {
      return getters['permissions'].includes('PROTOTYPE_EXPRESSIONS')
    },
    canViewDashboards (state, getters) {
      return getters['permissions'].includes('VIEW_DASHBOARDS')
    },
    canManageDashboards (state, getters) {
      return getters['permissions'].includes('MANAGE_DASHBOARDS')
    },
    canAutoCreateDefinitions (state, getters) {
      return getters['permissions'].includes('MANAGE_DASHBOARDS')
    },
    canAutoCreateExpressions (state, getters) {
      return getters['permissions'].includes('CREATE_EXPRESSIONS')
    },
    canViewConsolidation (state, getters) {
      return getters['permissions'].includes('VIEW_CONSOLIDATION_TABLES')
    },
    canUploadForecasts (state, getters) {
      return getters['permissions'].includes('UPLOAD_BUDGETS')
    },
    canViewIntegrations (state, getters) {
      return getters['permissions'].includes('VIEW_INTEGRATIONS')
    },
    canViewUsers (state, getters) {
      return getters['permissions'].includes('MANAGE_USERS')
    },
    canViewSavedStates (state, getters) {
      return getters['permissions'].includes('VIEW_DASHBOARD_SAVED_STATES')
    },
    userPermissions (state, getters) {
      return getters['permissions']
    },
    featureFlags (state) {
      return state.featureFlagsData || []
    },
    featureFlagsCompanyId (state) {
      return state.featureFlagsCompanyId
    },
    defaultRoute (state, getters, rootState, rootGetters) {
      if (!rootGetters['auth/isLoggedIn']) {
        return '/login'
      }
      if (getters.companyId === null) {
        return '/404'
      }
      let companyPath = `/company/${getters.companyId}`
      if (getters.canManageDashboards) {
        return `${companyPath}/reports`
      }
      if (getters.canViewDashboards) {
        return `${companyPath}/reports`
      }
      if (getters.canViewChecks) {
        return `${companyPath}/checks`
      }
      return `${companyPath}/settings/userSettings`
    },
  },
  mutations: {
    setFeatureFlags (state, flags) {
      state.featureFlagsData = flags || []
    },
    setFeatureFlagsCompanyId (state, companyId) {
      state.featureFlagsCompanyId = companyId || null
    },
    setPreviewRole (state, role) {
      state.previewRoleData = role
    },
    clearPreviewRole (state) {
      state.previewRoleData = null
    },
    setUser (state, user) {
      state.userObject = user
      Vue.prototype.$tracker.group(
        'company',
        user.company_users[0]?.company?.id,
        {
          company_name: user.company_users[0]?.company?.name,
        }
      )
      Vue.prototype.$tracker.identify(user.id, {
        email: user.email,
        name: user.name,
        company_id: user.company_users[0]?.company?.id,
        company_name: user.company_users[0]?.company?.name,
      })
    },
  },
  actions: {
    async loadUser (context, companyId) {
      if (!context.rootGetters['auth/isLoggedIn']) {
        return
      }
      let token = context.rootGetters['auth/authToken']
      let validatedLogin = await axios.get(
        `${process.env.VUE_APP_ADMIN_API_URL}/login`,
        {
          headers: { authorization: token, ...getTrackingHeaders(), },
          // admin-api only merges a company's flag overrides when told which
          // company is in view; without it every company-level flag reads off.
          params: companyId ? { company_id: companyId, } : {},
        }
      )
      if (validatedLogin.data.valid == true) {
        context.commit('setUser', validatedLogin.data.user)
        context.commit('setFeatureFlags', validatedLogin.data.feature_flags)
        context.commit('setFeatureFlagsCompanyId', companyId)
      }
      return
    },
  },
}
